/**
 * Generic column cell renderer for CRM list tables.
 *
 * Renders cells based on the `kind` field:
 * - 'object_name': Avatar + label
 * - 'property': PropertyValueLabel
 * - 'association': EntityBadgeGroup
 */

import { AlertCircle } from '@untitledui/icons';

import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import {
  EntityBadgeByIds,
  type EntityBadgeData,
  EntityBadgeGroup,
} from '../../components/badges';
import { CrmObjectAvatar } from '../../components/CrmObjectAvatar';
import {
  type EnrichmentStatus,
  isEnrichableProperty,
  isPropertyEmpty,
} from '../../components/enrichment';
import { PropertyValueLabel } from '../../components/properties/labels';
import type { ColumnConfig } from '../../core/types';
import type { PropertyTypeSchema } from '../../hooks/usePropertySchema';
import { getDisplayName } from '../../utils/getDisplayName';
import { LookupCell } from '../components/LookupCell';
import { EmailInitiatorCell } from './EmailInitiatorCell';
import { getLookupColumnParts } from './lookupColumn';

/**
 * Options for rendering a column cell with enrichment status.
 */
export interface RenderColumnCellOptions {
  /** Enrichment status for the contact ('idle' | 'pending' | 'failed') */
  enrichmentStatus?: EnrichmentStatus;
  /** Whether the object is a contact (only contacts support enrichment) */
  isContact?: boolean;
  /**
   * Org id used by association cells to resolve target entity ids via the
   * CRM store. When undefined (e.g. in unit tests that don't exercise the
   * live path), association cells fall back to the pre-resolved
   * `associatedObjects` map.
   */
  orgId?: number;
  /**
   * Property type schemas for lookup targets, keyed by the target entity's
   * `entity_type_name` and then by property name. Used by lookup cells to
   * resolve the schema for the target's property value.
   */
  lookupTargetSchemasByType?: Record<
    string,
    Record<string, PropertyTypeSchema>
  >;
  /** Maps entity_type_name -> human label, used by `kind: 'type'` cells on
   *  kind-based lists that span multiple entity types. */
  entityTypeLabels?: Record<string, string>;
}

/**
 * Render a table cell based on column configuration.
 *
 * @param column - Column configuration
 * @param object - CRM object to render
 * @param propertySchemas - Property type schemas for PropertyValueLabel
 * @param associatedObjects - Map of entity_id to CrmObject for association
 *   rendering. Used as a fallback when `options.orgId` is not provided.
 * @param isFirstProperty - Whether this is the first property column (applies bold styling)
 * @param options - Additional options including enrichment status and orgId
 * @returns React node for the cell
 */
export function renderColumnCell(
  column: ColumnConfig,
  object: CrmObject,
  propertySchemas: Record<string, PropertyTypeSchema>,
  associatedObjects?: Record<string, CrmObject>,
  isFirstProperty?: boolean,
  options?: RenderColumnCellOptions
): React.ReactNode {
  switch (column.kind) {
    case 'object_name':
      return renderObjectName(object);

    case 'property':
      return renderProperty(
        column,
        object,
        propertySchemas,
        isFirstProperty,
        options
      );

    case 'association':
      return renderAssociation(
        column,
        object,
        options?.orgId,
        associatedObjects
      );

    case 'lookup':
      return renderLookup(
        column,
        object,
        options?.orgId,
        options?.lookupTargetSchemasByType
      );

    case 'email_initiator':
      return (
        <EmailInitiatorCell
          object={object}
          propertySchemas={propertySchemas}
          orgId={options?.orgId}
        />
      );

    case 'type':
      return renderEntityType(object, options?.entityTypeLabels);
  }
}

/**
 * Render a row's entity-type label (e.g. "Event Automation"). Used by
 * kind-based lists that span multiple entity types so each row shows which
 * concrete type it is. Never falls back to the raw `entity_type_name` slug
 * (e.g. `ahoy_event_automation`) — that leaks internal ontology to users.
 */
function renderEntityType(
  object: CrmObject,
  entityTypeLabels?: Record<string, string>
): React.ReactNode {
  const label = entityTypeLabels?.[object.entity_type_name];
  if (!label) {
    return <span className='text-fg-quaternary'>—</span>;
  }
  return <span className='truncate text-sm text-fg-secondary'>{label}</span>;
}

/**
 * Render object name with avatar (primary identifier column).
 */
function renderObjectName(object: CrmObject): React.ReactNode {
  const label = getDisplayName(object, 'Unnamed');

  return (
    <div className='flex items-center gap-3 min-w-0'>
      <CrmObjectAvatar object={object} size='xs' />
      <div className='font-medium text-primary truncate'>{label}</div>
    </div>
  );
}

/**
 * Render property value using PropertyValueLabel.
 * For enrichable properties on contacts with pending enrichment, shows a spinner.
 */
function renderProperty(
  column: ColumnConfig,
  object: CrmObject,
  propertySchemas: Record<string, PropertyTypeSchema>,
  bold?: boolean,
  options?: RenderColumnCellOptions
): React.ReactNode {
  const propertyName = column.name;
  if (!propertyName) {
    return <span className='text-fg-quaternary'>—</span>;
  }
  const rawValue = getPropertyValue(object.properties?.[propertyName]);
  const schema = propertySchemas[propertyName];

  // Check if this property should show enrichment status
  const { enrichmentStatus, isContact } = options || {};
  const shouldShowEnrichmentStatus =
    isContact &&
    isEnrichableProperty(propertyName) &&
    isPropertyEmpty(rawValue) &&
    enrichmentStatus &&
    enrichmentStatus !== 'idle';

  // Show spinner for pending enrichment on empty enrichable properties
  if (shouldShowEnrichmentStatus && enrichmentStatus === 'pending') {
    return (
      <Tooltip title='Enrichment in progress' placement='top'>
        <TooltipTrigger>
          <span className='inline-flex items-center gap-1.5 text-fg-quaternary'>
            <svg
              className='w-3.5 h-3.5 animate-spin'
              viewBox='0 0 32 32'
              fill='none'
            >
              <circle
                className='text-bg-tertiary'
                cx='16'
                cy='16'
                r='14'
                stroke='currentColor'
                strokeWidth='4'
              />
              <circle
                className='stroke-fg-brand-primary'
                cx='16'
                cy='16'
                r='14'
                fill='none'
                strokeWidth='4'
                strokeDashoffset='75'
                strokeDasharray='70 30'
                strokeLinecap='round'
              />
            </svg>
            <span>Enriching…</span>
          </span>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  // Show error icon for failed enrichment on empty enrichable properties
  if (shouldShowEnrichmentStatus && enrichmentStatus === 'failed') {
    return (
      <Tooltip title='Enrichment failed' placement='top'>
        <TooltipTrigger>
          <span className='inline-flex items-center gap-1.5 text-utility-error-500'>
            <AlertCircle className='w-3.5 h-3.5' />
            <span>Failed</span>
          </span>
        </TooltipTrigger>
      </Tooltip>
    );
  }

  return (
    <PropertyValueLabel
      value={rawValue}
      propertyType={schema}
      fontSize='table'
      truncate
      bold={bold}
    />
  );
}

/**
 * Render association as an entity badge group.
 *
 * When `orgId` is available, uses `EntityBadgeByIds` so the cell:
 *   - Reflects WebSocket-driven association changes within one frame
 *     (live store subscription).
 *   - Renders a skeleton while a missing target is being fetched and a
 *     failed-state pill once the fetch settles without resolving
 *     (deleted entity, 404) — never a perpetual spinner.
 *   - Doesn't depend on `associated_objects` being in the React Query
 *     response, which used to leave the cell stuck on '—' after an edit
 *     until the list refetched.
 *
 * Falls back to the pre-resolved `associatedObjects` map only when
 * `orgId` is undefined (callers outside an org context, e.g. unit tests
 * that don't exercise the live path).
 */
function renderAssociation(
  column: ColumnConfig,
  object: CrmObject,
  orgId: number | undefined,
  associatedObjects?: Record<string, CrmObject>
): React.ReactNode {
  const associationTypeName = column.name;

  if (!associationTypeName) {
    return <span className='text-fg-quaternary'>—</span>;
  }

  // Find target ids for the requested association type.
  const targetIds =
    object.associations
      ?.filter(a => a.association_type_name === associationTypeName)
      .map(a => a.target_entity_id) ?? [];

  if (targetIds.length === 0) {
    return <span className='text-fg-quaternary'>—</span>;
  }

  // Preferred path: hydrate via the CRM store so cells stay live with
  // WebSocket events and don't depend on the list query carrying a
  // pre-resolved `associated_objects` map.
  //
  // `interactive` defaults to `true` so clicking a badge opens the
  // **associated entity's** slideout (e.g. clicking a "Works at" company
  // badge in the contacts table opens the company, not the row's contact).
  // EntityBadge's handleClick stops propagation, so the row's click handler
  // never fires for badge clicks. Clicking elsewhere on the cell still
  // bubbles to the row → opens the row's contact, preserving the
  // dominant row-click behavior.
  if (orgId && orgId > 0) {
    return (
      <EntityBadgeByIds
        entityIds={targetIds}
        orgId={orgId}
        variant='xs'
        maxVisible={1}
        wrap={false}
      />
    );
  }

  // Legacy fallback: use the pre-resolved map. Same behavior as before
  // for callers that don't have an org id.
  const fallbackObjects = targetIds
    .map(id => associatedObjects?.[id])
    .filter((obj): obj is CrmObject => obj !== undefined);

  if (fallbackObjects.length === 0) {
    return <span className='text-fg-quaternary'>—</span>;
  }

  const badgeData: EntityBadgeData[] = fallbackObjects.map(obj => ({
    id: obj.entity_id,
    entityType: obj.entity_type_name,
    displayName: getDisplayName(obj),
  }));

  // Legacy fallback path. Note: `EntityBadgeGroup` here has no `onClick`
  // wired (no `orgId` to construct slideout pushes), so badges remain
  // effectively static even with `interactive` defaulting to true —
  // EntityBadge needs both `interactive` AND an `onClick` to register as
  // clickable. Removing the explicit `interactive={false}` keeps the
  // signature minimal without changing behavior.
  return (
    <EntityBadgeGroup
      state={{ kind: 'data', data: badgeData }}
      variant='xs'
      maxVisible={1}
      wrap={false}
    />
  );
}

/**
 * Render a lookup column cell — the target object's property value(s)
 * reached via an association, hydrated from the CRM store.
 */
function renderLookup(
  column: ColumnConfig,
  object: CrmObject,
  orgId: number | undefined,
  schemas: Record<string, Record<string, PropertyTypeSchema>> | undefined
): React.ReactNode {
  const parts = getLookupColumnParts(column);
  if (!parts) {
    return <span className='text-fg-quaternary'>—</span>;
  }
  return (
    <LookupCell
      object={object}
      associationTypeName={parts.associationTypeName}
      targetPropertyName={parts.targetPropertyName}
      orgId={orgId}
      targetSchemasByType={schemas}
    />
  );
}
